import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { NumberParam, useQueryParams, withDefault } from 'use-query-params';
import axios from 'axios';
import { Col, CustomInput, FormGroup, Label, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { useAuth } from '../../../commons/Auth';
import { useLoading } from '../../../commons/Loading';
import BurgerMenu from '../../../commons/BurgerMenu';
import Footer from '../../../commons/Footer';
import Header from '../../../commons/Header';
import { ReactComponent as IconDocument } from '../../../images/icn_document.svg';
import { ReactComponent as IconBack } from '../../../images/icn-back.svg';
import { BtnOutlineOrange, ContFilterYear } from '../../../styled-components';
import variables from '../../../variables';
import EnergyRecurringSuppliersTable from './EnergyRecurringSuppliersTable';
import EnergySuppliersTable from './EnergySuppliersTable';
import InvoiceSuppliersTable from './InvoiceSuppliersTable';
import SignalSuppliersTable from './SignalSuppliersTable';
import SummaryTable from './SummaryTable';
import generateYearOptions from '../../../lib/helpers/generateYearOptions';
import { formatDateTime } from '../../../lib/helpers/date';

function InfoPresentational({ profile }) {
  const history = useHistory();
  const [{ year }, setQueryParams] = useQueryParams({
    year: withDefault(NumberParam, new Date().getFullYear()),
  });
  const [{ newToken } = {}] = useAuth();
  const [t] = useTranslation();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();
  const [suppliers, setSuppliers] = useState([]);
  const [energySuppliers, setEnergySuppliers] = useState([]);
  const [energyRecurringSuppliers, setEnergyRecurringSuppliers] = useState([]);
  const [signalSuppliers, setSignalSuppliers] = useState([]);
  const [updatedAt, setUpdatedAt] = useState();
  const [adminShareNotCollectedTots, setAdminShareNotCollectedTots] = useState({
    invoices: 0,
    partnerEon: 0,
    recurringFee: 0,
    signal: 0,
  });

  const totalInvoices = suppliers.reduce(
    (acc, supplier) => acc + Number(supplier?.adminShare || 0),
    0
  );
  const totalEnergy = energySuppliers.reduce(
    (acc, supplier) => acc + Number(supplier?.adminShare || 0),
    0
  );
  const totalEnergyRecurring = energyRecurringSuppliers.reduce(
    (acc, supplier) => acc + Number(supplier?.adminShare || 0),
    0
  );
  const totalSignals = signalSuppliers.reduce(
    (acc, supplier) => acc + Number(supplier?.adminShare || 0),
    0
  );

  const summaryRows = [
    {
      service: 'Compensi da fatture fornitore',
      total: totalInvoices,
      toEmit: adminShareNotCollectedTots.invoices - totalInvoices,
    },
    {
      service: 'Compensi da fee energia',
      total: totalEnergy,
      toEmit: adminShareNotCollectedTots.partnerEon - totalEnergy,
    },
    {
      service: 'Compensi da ricorrenze energia',
      total: totalEnergyRecurring,
      toEmit: adminShareNotCollectedTots.recurringFee - totalEnergyRecurring,
    },
    {
      service: 'Compensi da segnalazioni',
      total: totalSignals,
      toEmit: adminShareNotCollectedTots.signal - totalSignals,
    },
  ];

  const onYearChange = (event) => {
    const selectedYear = Number(event.target.value);
    setQueryParams({ year: selectedYear });
  };

  useEffect(() => {
    if (!profile?._id || !newToken) return undefined;

    let isMounted = true;
    const fetchSuppliers = async () => {
      showLoading();

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/give-and-take/${profile._id}`,
          {
            headers: { Authorization: newToken },
            params: { year },
          }
        );

        if (response instanceof Error) {
          alert.error(
            response?.response?.data?.message || t('COMMON:GENERIC_ERROR')
          );
          return;
        }

        const annualSummary = Array.isArray(response.data)
          ? response.data[0]
          : undefined;
        if (isMounted) {
          setUpdatedAt(annualSummary?.updatedAt);
          setSuppliers(
            Array.isArray(annualSummary?.suppliers)
              ? annualSummary.suppliers
              : []
          );
          setEnergySuppliers(
            Array.isArray(annualSummary?.energySuppliers)
              ? annualSummary.energySuppliers
              : []
          );
          setEnergyRecurringSuppliers(
            Array.isArray(annualSummary?.energyRecurringSuppliers)
              ? annualSummary.energyRecurringSuppliers
              : []
          );
          setSignalSuppliers(
            Array.isArray(annualSummary?.signalSuppliers)
              ? annualSummary.signalSuppliers
              : []
          );
          setAdminShareNotCollectedTots({
            invoices: Number(
              annualSummary?.invoices?.adminShareNotCollectedTot || 0
            ),
            partnerEon: Number(
              annualSummary?.partnerEon?.adminShareNotCollectedTot || 0
            ),
            recurringFee: Number(
              annualSummary?.recurringFee?.adminShareNotCollectedTot || 0
            ),
            signal: Number(
              annualSummary?.signal?.adminShareNotCollectedTot || 0
            ),
          });
        }
      } catch (error) {
        if (isMounted) alert.error(t('COMMON:GENERIC_ERROR'));
      } finally {
        hideLoading();
      }
    };

    fetchSuppliers();
    return () => {
      isMounted = false;
    };
  }, [profile?._id, newToken, year]);

  useEffect(() => {
    const anchorId = window.location.hash.split('#').pop();
    if (!anchorId) return undefined;

    const timeoutId = window.setTimeout(() => {
      document.getElementById(anchorId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [suppliers, energySuppliers, energyRecurringSuppliers, signalSuppliers]);

  return (
    <div className="App">
      <BurgerMenu />
      <div className="corpo">
        <section>
          <Header
            title="Dare e avere - Informazioni"
            icon={<IconDocument style={{ height: '25px', color: '#ffffff' }} />}
          />
          <Row className="m-t-20" style={{ marginBottom: '20px ' }}>
            <Col xs="12">
              <BtnOutlineOrange
                type="button"
                onClick={() => history.push(`/my-give-and-take?year=${year}`)}
              >
                <IconBack fill={variables.primary} /> Torna a Dare e avere
              </BtnOutlineOrange>
            </Col>
          </Row>

          <Row
            className="search-add-cont m-t-20"
            style={{ rowGap: '1rem', marginBottom: '1rem' }}
          >
            <Col sm="12" md="6" lg="3" className="select-wrapper">
              <ContFilterYear
                className="no-width"
                style={{ marginBottom: '0' }}
              >
                <Row>
                  <Col xs="6">
                    <Label>{t('GIVE_AND_TAKE:SEARCH_BY_YEAR')}</Label>
                  </Col>
                  <Col xs="6">
                    <FormGroup>
                      <CustomInput
                        type="select"
                        id="selectYear"
                        name="year"
                        value={year}
                        onChange={onYearChange}
                      >
                        {generateYearOptions()}
                      </CustomInput>
                    </FormGroup>
                  </Col>
                </Row>
              </ContFilterYear>
            </Col>
            {updatedAt && (
              <Col
                sm="12"
                md="6"
                lg="9"
                className="text-right"
                style={{ alignSelf: 'center', fontWeight: 'bold' }}
              >
                {t('GIVE_AND_TAKE:LAST_UPDATED')}: {formatDateTime(updatedAt)}
              </Col>
            )}
          </Row>
          <div>
            <SummaryTable title="Riepilogo totali" rows={summaryRows} />
            <InvoiceSuppliersTable
              suppliers={suppliers}
              remainedTotal={adminShareNotCollectedTots.invoices}
            />
            <EnergySuppliersTable
              suppliers={energySuppliers}
              remainedTotal={adminShareNotCollectedTots.partnerEon}
            />
            <EnergyRecurringSuppliersTable
              suppliers={energyRecurringSuppliers}
              remainedTotal={adminShareNotCollectedTots.recurringFee}
            />
            <SignalSuppliersTable
              suppliers={signalSuppliers}
              remainedTotal={adminShareNotCollectedTots.signal}
            />
          </div>
        </section>
        <Footer />
      </div>
    </div>
  );
}

export default InfoPresentational;
